import { isApacCountry } from '../lib/countries'

import type { WardrobeItem } from '../types/wardrobe'

/**
 * Translation keys for the nine governed sections the wardrobe trait emits
 * (RP-5934), plus `Other` — not a section the trait sends, but the bucket
 * `getWardrobeGroupKey` falls back to, which needs a heading like any other.
 *
 * Only the key is held here — the display order is the trait's, so that
 * reordering the wardrobe stays a trait-side change with no frontend release,
 * which is the whole point of the ordered payload.
 */
const SECTION_TRANSLATION_KEYS: Record<string, string> = {
  Other: 'wardrobe.sections.other',
  Suits: 'wardrobe.sections.suits',
  Waistcoats: 'wardrobe.sections.waistcoats',
  Blazers: 'wardrobe.sections.blazers',
  Shirts: 'wardrobe.sections.shirts',
  Trousers: 'wardrobe.sections.trousers',
  Knitwear: 'wardrobe.sections.knitwear',
  Outerwear: 'wardrobe.sections.outerwear',
  Footwear: 'wardrobe.sections.footwear',
  Accessories: 'wardrobe.sections.accessories',
}

/**
 * What the tab groups an item under.
 *
 * `section` is the governed value and the one to use. The `category` fallback
 * covers an environment still serving the pre-RP-5934 trait, where `category`
 * carried the department and grouping on it was correct.
 *
 * Never returns an empty key: an item the payload gives us no way to classify
 * still belongs somewhere the customer can see it, and an empty key would put
 * it back under the blank heading this whole change exists to remove.
 */
export function getWardrobeGroupKey(item: WardrobeItem): string {
  return item.section || item.category || 'Other'
}

/**
 * Translated heading for a wardrobe section.
 *
 * Falls back to the raw section name twice over: for a section the trait adds
 * that we have no key for, and for a key with no translation in the active
 * language — `t` returns the key itself when it can't resolve one, and
 * `wardrobe.sections.suits` on screen is worse than `Suits`.
 */
export function getWardrobeSectionLabel(
  section: string,
  t: (key: string) => string,
): string {
  const key = SECTION_TRANSLATION_KEYS[section]
  if (!key) return section

  const label = t(key)
  return label === key ? section : label
}

/**
 * Whether a wardrobe item is Custom Made.
 *
 * Keyed on `type`, which the payload always sends, rather than on the presence
 * of the optional `custom_made` object. The two disagree: an item can arrive
 * with `type: 'custom_made'` and no `custom_made` details — the display-name
 * fallback below exists for exactly that case — and the analytics event used to
 * read the object, so such an item showed the Custom Made badge while reporting
 * `is_custom_made: false`. One definition, so the badge and the event cannot
 * drift apart again.
 */
export function isCustomMadeItem(item: WardrobeItem): boolean {
  return item.type === 'custom_made'
}

/**
 * RTW items use the top-level `name`. Custom Made items only expose a generic
 * category as `name` (e.g. "Trousers"), so prefer `custom_made.fabric_name`.
 */
export function getWardrobeItemDisplayName(item: WardrobeItem): string {
  if (item.type === 'custom_made') {
    return item.custom_made?.fabric_name || item.name
  }
  return item.name
}

/**
 * Prefer the nested custom-made fabric name when present; otherwise fall back
 * to the top-level material field.
 */
export function getWardrobeItemMaterial(item: WardrobeItem): string {
  if (item.type === 'custom_made') {
    return item.custom_made?.fabric_name || item.material
  }
  return item.material
}

/**
 * Custom Made items have no RTW size.
 * Pass the translated label (e.g. `t('wardrobe.customSize')`) as
 * `customSizeLabel`; defaults to the English string so existing tests and
 * non-i18n call sites continue to work.
 *
 * `countryCode` selects which of the payload's regional sizes to read. In an
 * APAC market the top-level `size` is the wrong reference — increasingly so as
 * the legacy A-fits are retired (MEAS-8327) — so the item's own `sizes.apac` is
 * preferred there. Everywhere else, and whenever the payload carries no APAC
 * size for the item, the default `size` stands: showing nothing where a size
 * exists would be a worse answer than showing the default one.
 *
 * Callers gate this by role: the regional lookup is customer-facing only, and
 * in-store style advisors get the default size in every market. Pass no
 * `countryCode` for them — see `WardrobeItemModal`.
 */
export function getWardrobeItemSize(
  item: WardrobeItem,
  customSizeLabel = 'Custom Size',
  countryCode?: string | null,
): string | null {
  if (item.type === 'custom_made') {
    return customSizeLabel
  }
  if (isApacCountry(countryCode)) {
    return item.sizes?.apac ?? item.size
  }
  return item.size
}
